import React, { useState, useEffect, useCallback } from 'react';
import {
  KeyRound, RefreshCw, Plus, Copy, Check, Trash2, ShieldOff, ShieldCheck, X, Mail,
} from 'lucide-react';
import api from '../../services/api';

// Mirror of backend/config/api-scopes.js — keep in sync if scopes change.
const SCOPES = [
  { value: 'traders:read',        label: 'Read traders',        hint: 'Leaderboard & performance — your own traders' },
  { value: 'traders:manage',      label: 'Manage traders',      hint: 'Their OWN traders: positions, followers, profit share' },
  { value: 'copiers:read',        label: 'Read copiers',        hint: 'Your copiers\' data' },
  { value: 'users:provision',     label: 'Provision users',     hint: 'Onboard + spin up MT5' },
  { value: 'relationships:write', label: 'Manage relationships', hint: 'Follow / pause / unfollow' },
];
const TIERS = ['standard', 'high', 'unlimited'];
// Real-only is the reason this exists: a partner who sells a live-trading
// product does not want demo accounts consuming a container each.
const ACCOUNT_POLICIES = [
  { value: 'any',       label: 'demo + real' },
  { value: 'real_only', label: 'real only' },
  { value: 'demo_only', label: 'demo only' },
];

function relTime(iso) {
  if (!iso) return 'never';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return `${Math.round(s)}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function ScopePill({ s }) {
  return (
    <span style={{
      display: 'inline-block', padding: '1px 7px', borderRadius: 4, marginRight: 4,
      background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)',
      fontFamily: 'var(--font-mono, monospace)', fontSize: 11, color: 'var(--accent)',
    }}>{s}</span>
  );
}

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null); // partner being managed (keys panel)
  const [branding, setBranding] = useState(null); // partner being white-labelled (mail panel)

  const load = useCallback(async () => {
    try {
      const r = await api.get('/admin/partners');
      setPartners(r.data.partners || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { setLoading(true); load(); }, [load]);

  async function updatePartner(id, patch) {
    try {
      await api.put(`/admin/partners/${id}`, patch);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  }

  return (
    <>
      <div className="ct-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <KeyRound size={22} color="var(--accent)" /> Partner API
          </h1>
          <div className="subtitle">Affiliate partners &amp; their developer API keys</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a className="btn btn-sm btn-secondary" href="/api/v1/docs" target="_blank" rel="noopener noreferrer">API docs</a>
          <button className="btn btn-sm btn-secondary" onClick={load}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setShowNew(v => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> New Partner
          </button>
        </div>
      </div>

      {showNew && <NewPartnerForm onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? <p className="text-muted text-center py-4">Loading...</p> :
            partners.length === 0 ? (
              <p className="text-muted text-center py-5">No partners yet. Create one to issue API keys.</p>
            ) : (
              <div className="table-responsive table-cards table-cards-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Name</th><th>Tier</th><th>Accounts</th><th>Active keys</th><th>Users</th>
                      <th>Status</th><th>Created</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map(p => (
                      <tr key={p._id} style={p.status === 'suspended' ? { opacity: 0.55 } : {}}>
                        <td data-label="Name">
                          <strong>{p.name}</strong>
                          {p.contactEmail && <div className="text-muted small">{p.contactEmail}</div>}
                        </td>
                        <td data-label="Tier">
                          <select className="form-control form-control-sm" style={{ width: 120 }}
                            value={p.rateTier}
                            onChange={e => updatePartner(p._id, { rateTier: e.target.value })}>
                            {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </td>
                        <td data-label="Accounts">
                          {/* Which MT5 account types this partner's users may connect. Applies
                              to the servers list they can browse, the connect call itself, and
                              — authoritatively — to what the terminal reports once it boots. */}
                          <select className="form-control form-control-sm" style={{ width: 130 }}
                            value={p.accountTypePolicy || 'any'}
                            onChange={e => updatePartner(p._id, { accountTypePolicy: e.target.value })}>
                            {ACCOUNT_POLICIES.map(o =>
                              <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </td>
                        <td data-label="Active keys">{p.keyCount}</td>
                        <td data-label="Users">{p.userCount}{p.maxUsers > 0 ? ` / ${p.maxUsers}` : ''}</td>
                        <td data-label="Status">
                          {p.status === 'active'
                            ? <span style={{ color: 'var(--profit)', fontWeight: 600 }}>active</span>
                            : <span style={{ color: 'var(--loss)', fontWeight: 600 }}>suspended</span>}
                        </td>
                        <td data-label="Created" className="text-muted small">{relTime(p.createdAt)}</td>
                        <td data-label="Actions">
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button className="btn btn-sm btn-info"
                              onClick={() => { setBranding(null); setSelected(selected?._id === p._id ? null : p); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <KeyRound size={12} /> Keys
                            </button>
                            <button className="btn btn-sm btn-secondary"
                              onClick={() => { setSelected(null); setBranding(branding?._id === p._id ? null : p); }}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Mail size={12} /> Branding
                            </button>
                            {p.status === 'active' ? (
                              <button className="btn btn-sm btn-warning"
                                onClick={() => updatePartner(p._id, { status: 'suspended' })}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <ShieldOff size={12} /> Suspend
                              </button>
                            ) : (
                              <button className="btn btn-sm btn-success"
                                onClick={() => updatePartner(p._id, { status: 'active' })}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <ShieldCheck size={12} /> Activate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      </div>

      {selected && <KeysPanel partner={selected} onClose={() => setSelected(null)} onChange={load} />}
      {branding && <BrandingPanel partner={branding} onClose={() => setBranding(null)} onSaved={load} />}
    </>
  );
}

function NewPartnerForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: '', contactEmail: '', rateTier: 'standard', maxUsers: 0, accountTypePolicy: 'any',
  });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      await api.post('/admin/partners', {
        name: form.name.trim(),
        contactEmail: form.contactEmail.trim(),
        rateTier: form.rateTier,
        accountTypePolicy: form.accountTypePolicy,
        maxUsers: Number(form.maxUsers) || 0,
      });
      onCreated();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16, borderColor: 'var(--accent)' }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <strong>New Partner</strong>
          <button className="btn btn-sm btn-link" onClick={onClose} style={{ padding: 0 }}><X size={16} /></button>
        </div>
        <form onSubmit={submit} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label className="small text-muted">Name *</label>
            <input className="form-control form-control-sm" value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Acme Copy" required />
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label className="small text-muted">Contact email</label>
            <input className="form-control form-control-sm" type="email" value={form.contactEmail}
              onChange={e => setForm({ ...form, contactEmail: e.target.value })} placeholder="dev@acme.io" />
          </div>
          <div style={{ flex: '0 1 140px' }}>
            <label className="small text-muted">Rate tier</label>
            <select className="form-control form-control-sm" value={form.accountTypePolicy}
              onChange={e => setForm({ ...form, accountTypePolicy: e.target.value })}>
              {ACCOUNT_POLICIES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <select className="form-control form-control-sm" value={form.rateTier}
              onChange={e => setForm({ ...form, rateTier: e.target.value })}>
              {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{ flex: '0 1 130px' }}>
            <label className="small text-muted">Max users (0 = default)</label>
            <input className="form-control form-control-sm" type="number" min="0" value={form.maxUsers}
              onChange={e => setForm({ ...form, maxUsers: e.target.value })} />
          </div>
          <button className="btn btn-sm btn-primary" disabled={busy} type="submit">
            {busy ? 'Creating…' : 'Create'}
          </button>
        </form>
      </div>
    </div>
  );
}

function KeysPanel({ partner, onClose, onChange }) {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopes, setScopes] = useState(['traders:read']);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [minted, setMinted] = useState(null); // raw key shown once
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/partners/${partner._id}/keys`);
      setKeys(r.data.keys || []);
    } finally {
      setLoading(false);
    }
  }, [partner._id]);

  useEffect(() => { setLoading(true); setMinted(null); load(); }, [load]);

  function toggleScope(s) {
    setScopes(cur => cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]);
  }

  async function mint(e) {
    e.preventDefault();
    if (!scopes.length) return alert('Select at least one scope');
    setBusy(true);
    try {
      const r = await api.post(`/admin/partners/${partner._id}/keys`, { scopes, name: name.trim() });
      setMinted(r.data.key);
      setName('');
      await load();
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(keyId) {
    if (!window.confirm('Revoke this key? Any integration using it will immediately stop working.')) return;
    try {
      await api.delete(`/admin/partners/${partner._id}/keys/${keyId}`);
      await load();
      onChange?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  }

  function copyKey() {
    navigator.clipboard?.writeText(minted).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <KeyRound size={16} color="var(--accent)" /> Keys — {partner.name}
          </strong>
          <button className="btn btn-sm btn-link" onClick={onClose} style={{ padding: 0 }}><X size={16} /></button>
        </div>

        {/* Mint form */}
        <form onSubmit={mint} style={{ marginBottom: 16 }}>
          <div className="small text-muted" style={{ marginBottom: 6 }}>Scopes for new key</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
            {SCOPES.map(s => (
              <label key={s.value} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={scopes.includes(s.value)} onChange={() => toggleScope(s.value)} />
                <span><strong style={{ fontSize: 13 }}>{s.label}</strong>{' '}
                  <span className="text-muted small">— {s.hint}</span></span>
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px' }}>
              <label className="small text-muted">Key label (optional)</label>
              <input className="form-control form-control-sm" value={name}
                onChange={e => setName(e.target.value)} placeholder="prod server" />
            </div>
            <button className="btn btn-sm btn-primary" disabled={busy} type="submit"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Plus size={14} /> {busy ? 'Minting…' : 'Mint key'}
            </button>
          </div>
        </form>

        {/* One-time raw key reveal */}
        {minted && (
          <div style={{
            background: 'var(--bg-surface-2)', border: '1px solid var(--accent)',
            borderRadius: 8, padding: '12px 14px', marginBottom: 16,
          }}>
            <div className="small" style={{ color: 'var(--accent)', fontWeight: 600, marginBottom: 6 }}>
              Copy this key now — it won't be shown again.
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <code style={{
                flex: 1, wordBreak: 'break-all', fontSize: 13,
                background: 'var(--bg-surface)', padding: '8px 10px', borderRadius: 6,
              }}>{minted}</code>
              <button className="btn btn-sm btn-secondary" onClick={copyKey}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
              </button>
            </div>
          </div>
        )}

        {/* Existing keys */}
        {loading ? <p className="text-muted small">Loading keys…</p> :
          keys.length === 0 ? <p className="text-muted small">No keys yet.</p> : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr><th>Prefix</th><th>Label</th><th>Scopes</th><th>Last used</th><th>Status</th><th></th></tr>
                </thead>
                <tbody>
                  {keys.map(k => (
                    <tr key={k._id} style={k.revokedAt ? { opacity: 0.5 } : {}}>
                      <td data-label="Prefix"><code style={{ fontSize: 12 }}>{k.prefix}…</code></td>
                      <td data-label="Label" className="small">{k.name || '—'}</td>
                      <td data-label="Scopes">{(k.scopes || []).map(s => <ScopePill key={s} s={s} />)}</td>
                      <td data-label="Last used" className="text-muted small">
                        {relTime(k.lastUsedAt)}{k.lastUsedIp ? ` · ${k.lastUsedIp}` : ''}
                      </td>
                      <td data-label="Status">
                        {k.revokedAt
                          ? <span className="text-muted small">revoked {relTime(k.revokedAt)}</span>
                          : <span style={{ color: 'var(--profit)', fontWeight: 600 }}>active</span>}
                      </td>
                      <td data-label="">
                        {!k.revokedAt && (
                          <button className="btn btn-sm btn-danger"
                            onClick={() => revoke(k._id)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Trash2 size={12} /> Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

// Per-partner white-label mail. Everything here is optional: a blank field
// means "use the platform default", which is what every partner did before
// these settings existed. See backend/services/partner-mail.js.
function BrandingPanel({ partner, onClose, onSaved }) {
  const smtp = partner.smtp || {};
  const [form, setForm] = useState({
    websiteUrl: partner.websiteUrl || '',
    host:       smtp.host || '',
    port:       smtp.port || 587,
    secure:     !!smtp.secure,
    user:       smtp.user || '',
    fromEmail:  smtp.fromEmail || '',
    password:   '',
  });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setSaved(false); };

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/partners/${partner._id}`, {
        websiteUrl: form.websiteUrl.trim(),
        smtp: {
          host:      form.host.trim(),
          port:      Number(form.port) || 587,
          secure:    form.secure,
          user:      form.user.trim(),
          fromEmail: form.fromEmail.trim(),
          // Sent only when the admin actually typed one. The backend treats a
          // blank password as "unchanged" for the same reason, so editing the
          // host can never silently wipe the stored credential.
          ...(form.password ? { password: form.password } : {}),
        },
      });
      setForm(f => ({ ...f, password: '' }));
      setSaved(true);
      onSaved?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  const usingPlatformSmtp = !form.host.trim();

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-body">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <strong style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Mail size={16} color="var(--accent)" /> Email branding &mdash; {partner.name}
          </strong>
          <button className="btn btn-sm btn-link" onClick={onClose} style={{ padding: 0 }}><X size={16} /></button>
        </div>

        <p className="text-muted small" style={{ marginTop: -4, marginBottom: 16 }}>
          Mail to this partner&apos;s users is already signed <strong>{partner.name}</strong>.
          These settings control where those emails <em>link</em> and which server
          <strong> sends</strong> them. Leave anything blank to use the platform default.
        </p>

        <form onSubmit={save}>
          <div style={{ marginBottom: 18 }}>
            <label className="small text-muted">Partner website</label>
            <input className="form-control form-control-sm" type="url" placeholder="https://partner.example.com"
              value={form.websiteUrl} onChange={e => set('websiteUrl', e.target.value)} />
            <div className="text-muted small" style={{ marginTop: 5, lineHeight: 1.5 }}>
              Buttons in their users&apos; emails point here instead of our dashboard.
              We link the <strong>site root only</strong> &mdash; their site has its own
              routing, so appending our paths (<code>/copier/accounts</code>) would send
              readers to a 404. Blank = link to our dashboard as before.
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
              <strong style={{ fontSize: 13 }}>Outgoing mail server</strong>
              <span className="text-muted small">
                {usingPlatformSmtp
                  ? 'using the platform server (envelope is our domain)'
                  : `sending as ${form.fromEmail.trim() || form.user.trim() || 'the SMTP user'}`}
              </span>
            </div>
            <div className="text-muted small" style={{ marginBottom: 12, lineHeight: 1.5 }}>
              Set a host to send from the partner&apos;s own domain. Clear the host to turn
              this off. If their server rejects a message we fall back to the platform
              server rather than lose the email &mdash; check the backend log for
              <code> Partner SMTP send failed</code>.
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: '2 1 220px' }}>
                <label className="small text-muted">SMTP host</label>
                <input className="form-control form-control-sm" placeholder="smtp.partner.com"
                  value={form.host} onChange={e => set('host', e.target.value)} />
              </div>
              <div style={{ flex: '0 1 90px' }}>
                <label className="small text-muted">Port</label>
                <input className="form-control form-control-sm" type="number" min="1" max="65535"
                  value={form.port} onChange={e => set('port', e.target.value)} />
              </div>
              <div style={{ flex: '0 1 110px', alignSelf: 'flex-end', paddingBottom: 7 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.secure}
                    onChange={e => set('secure', e.target.checked)} />
                  <span className="small">TLS (465)</span>
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 10 }}>
              <div style={{ flex: '1 1 200px' }}>
                <label className="small text-muted">Username</label>
                <input className="form-control form-control-sm" placeholder="no-reply@partner.com"
                  value={form.user} onChange={e => set('user', e.target.value)} />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label className="small text-muted">
                  Password {smtp.passwordSet && <span style={{ color: 'var(--profit)' }}>&bull; stored</span>}
                </label>
                <input className="form-control form-control-sm" type="password" autoComplete="new-password"
                  placeholder={smtp.passwordSet ? 'leave blank to keep current' : ''}
                  value={form.password} onChange={e => set('password', e.target.value)} />
              </div>
              <div style={{ flex: '1 1 200px' }}>
                <label className="small text-muted">From address</label>
                <input className="form-control form-control-sm" type="email" placeholder="(defaults to username)"
                  value={form.fromEmail} onChange={e => set('fromEmail', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn btn-sm btn-primary" type="submit" disabled={busy}>
              {busy ? 'Saving...' : 'Save branding'}
            </button>
            {saved && (
              <span style={{ color: 'var(--profit)', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                className="small">
                <Check size={14} /> Saved
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
